import { useContent } from '@hooks/use-server-content';
import { RTL_LANGUAGES } from '@constants';
import AppConfigurationService from '@services/app-configuration-service/app-configuration-service';
import {
    FALLBACK_SECURITY_CONTENT_LOCALES,
    securityContentFallbackAr,
    securityContentFallbackEn,
} from './security-content-fallback';

export interface SecurityContent {
    returnButtonText: string;
    brandLogo: string;
    signInCredentialsSectionHeader: string;
    emailSectionLabel: string;
    passwordSectionLabel: string;
    passkeysSectionHeader: string;
    passkeysIconSubheadingOne: string;
    passkeysIconSubheadingTwo: string;
    passkeysIconSubheadingThree: string;
    passkeysPreDescription: string;
    passkeysPostDescription: string;
    passkeysIdText: string;
    passkeysCreatedText: string;
    passkeysCreateButton: string;
    passkeysRemoveButton: string;
    passkeysCreationSuccessNotificationHeader: string;
    passkeysCreationSuccessNotificationDescription: string;
    passkeysRemoveModalHeader: string;
    passkeysRemoveModalDescription: string;
    cancelButton: string;
    passkeysRemovalSuccessNotificationHeader: string;
    passkeysRemovalSuccessNotificationDescription: string;
    passkeysRemovalErrorNotificationHeader: string;
    passkeysRemovalErrorNotificationDescription: string;
    MFASectionHeader: string;
    MFADescription: string;
    MFAToggleHeader: string;
    MFAToggleOnLabelText: string;
    MFAToggleOffLabelText: string;
    MFAEnableModalHeaderText: string;
    MFAEnableModalBodyText: string;
    MFAEnableModalConfirmButtonLabelText: string;
    MFAEnableModalConfirmButtonAriaLabel: string;
    MFAEnableModalCancelButtonLabelText: string;
    MFAEnableModalCancelButtonAriaLabel: string;
    MFAEnableModalCloseButtonAriaLabel: string;
    MFADisableModalHeaderText: string;
    MFADisableModalBodyText: string;
    MFADisableModalConfirmButtonLabelText: string;
    MFADisableModalConfirmButtonAriaLabel: string;
    MFADisableModalCancelButtonLabelText: string;
    MFADisableModalCancelButtonAriaLabel: string;
    MFADisableModalCloseButtonAriaLabel: string;
    MFAEmailAuthenticationSectionLabel: string;
    MFAEmailAuthenticationDescriptionText: string;
    MFAAppAuthenticationSectionLabel: string;
    MFAAppAuthenticationDescriptionText: string;
    MFASmsAuthenticationSectionLabel: string;
    MFASmsAuthenticationDescriptionText: string;
    editButtonText: string;
    addButtonText: string;
    removeButtonText: string;
    mfaGetOptionsErrorNotificationMessage: string;
    mfaUpdateUserLevelFlagErrorNotificationMessage: string;
    mfaDeleteOptionErrorNotificationMessage: string;
    errorPageReturnButtonText: string;
    errorPageReturnButtonAriaLabel: string;
    errorPageAlertIcon: string;
    errorPageHeaderText: string;
    errorPageDescriptionText: string;
    errorPageCancelButtonText: string;
    errorPageCancelButtonAriaLabel: string;
    signInSecurityButtonText: string;
    MFAEmailChangeWarningModalHeaderText: string;
    MFAEmailChangeWarningModalBodyText: string;
    MFAEmailChangeWarningModalContinueButton: string;
    MFAEmailChangeWarningModalContinueButtonAriaLabel: string;
    MFAEmailChangeWarningModalCancelButton: string;
    MFAEmailChangeWarningModalCancelButtonAriaLabel: string;
}

export const useSecurityContent = (): SecurityContent | null => {
    const [content, getValueByName] = useContent('common', 'security');
    const contentFragment: any = {};

    if (content) {
        content?.elements?.forEach((element) => {
            contentFragment[`${element.name}`] = getValueByName(
                `${element.name}`
            );
        });
    }

    if (!Object.keys(contentFragment).length) {
        const locale = new AppConfigurationService()
            .getLanguageRegionCode()
            ?.toLowerCase();

        if (FALLBACK_SECURITY_CONTENT_LOCALES.includes(locale)) {
            return RTL_LANGUAGES.includes(locale)
                ? securityContentFallbackAr
                : securityContentFallbackEn;
        }

        return null;
    }

    return contentFragment as SecurityContent;
};
